import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, AlertCircle, CalendarClock, TrendingUp } from 'lucide-react';
import { useQuery } from 'react-query';
import SEO from '../components/SEO';
import { programsAPI, cmsAPI } from '../services/api';
import {
  formatScheduleDate,
  formatScheduleTime,
  getProgramScheduleEntries,
  getProgramScheduleSummary,
  getProgramScheduleDayTime,
} from '../utils/programSchedules';

const TRACK_LABELS: Record<string, { label: string; icon: string }> = {
  robotics: { label: 'Robotics Engineering', icon: '🤖' },
  cs_ai: { label: 'Computer Science & AI', icon: '💻' },
};

const ProgramsPage: React.FC = () => {
  const [activeTrack, setActiveTrack] = useState<string>('all');

  // Fetch site settings
  const { data: settings } = useQuery('siteSettings', async () => {
    const response = await cmsAPI.getSettings();
    return response.data;
  });

  // Fetch programs from database
  const { data: programs = [], isLoading } = useQuery('programs', async () => {
    const response = await programsAPI.getPrograms();
    return response.data?.results || response.data || [];
  });

  const programsSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "STEM Programs for Kids",
    "description": "Robotics, Coding, and AI programs for children ages 7-14",
    "numberOfItems": programs.length,
    "itemListElement": programs.slice(0, 5).map((program: any, index: number) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Course",
        "name": program.name,
        "description": program.description,
        "provider": {
          "@type": "EducationalOrganization",
          "name": "Future Minds Academy"
        }
      }
    }))
  };

  return (
    <div className="pt-28">
      <SEO 
        title="STEM Programs | Robotics & Coding Classes for Kids"
        description="Explore our STEM programs: robotics classes, coding courses, and AI education for children ages 7-14. Hands-on learning in Clayton, NC. View schedules and enroll."
        keywords="robotics programs for kids, coding classes children, STEM courses Clayton NC, kids programming classes, AI classes for kids, after school robotics"
        canonical="/programs"
        structuredData={programsSchema}
      />
      {/* Hero */}
      <section className="gradient-bg pt-12 pb-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">Our Programs</h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Explore our hands-on STEM programs designed for curious young minds.
            </p>
            <div className="mt-8 inline-flex items-start gap-3 bg-white/15 backdrop-blur rounded-xl px-6 py-4 text-left max-w-2xl">
              <CalendarClock className="flex-shrink-0 mt-0.5" size={22} />
              <p className="text-white/90 text-sm md:text-base">
                <span className="font-semibold">Enroll anytime!</span> You can join a program whenever you want —
                even if it has already started. Your first month is prorated, so you only pay for the sessions
                remaining in the month, then the regular monthly price after that.
              </p>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Programs List - Dynamic */}
      <section className="section">
        <div className="container-custom">
          {/* Track Tabs */}
          {(() => {
            const tracks = Array.from(new Set(programs.map((p: any) => p.track || 'robotics'))) as string[];
            return tracks.length > 1 ? (
              <div className="flex justify-center mb-10">
                <div className="inline-flex bg-gray-100 rounded-xl p-1.5">
                  <button
                    onClick={() => setActiveTrack('all')}
                    className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all ${
                      activeTrack === 'all' ? 'bg-white shadow-md text-primary-600' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    All Programs
                  </button>
                  {tracks.map((track: string) => (
                    <button
                      key={track}
                      onClick={() => setActiveTrack(track)}
                      className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                        activeTrack === track ? 'bg-white shadow-md text-primary-600' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <span>{TRACK_LABELS[track]?.icon || '📚'}</span>
                      {TRACK_LABELS[track]?.label || track}
                    </button>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-8"
            >

              {programs.filter((p: any) => activeTrack === 'all' || (p.track || 'robotics') === activeTrack).length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                  <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Programs Available</h3>
                  <p className="text-gray-600">Programs will be added soon by administrators.</p>
                </div>
              ) : (
                Object.entries(
                  programs
                    .filter((p: any) => activeTrack === 'all' || (p.track || 'robotics') === activeTrack)
                    .reduce((groups: Record<string, any[]>, p: any) => {
                      const track = p.track || 'robotics';
                      (groups[track] = groups[track] || []).push(p);
                      return groups;
                    }, {})
                )
                .sort(([a], [b]) => ['robotics', 'cs_ai'].indexOf(a) - ['robotics', 'cs_ai'].indexOf(b))
                .map(([track, trackPrograms]) => {
                  const sorted = [...(trackPrograms as any[])].sort(
                    (a, b) => (a.level_number ?? 99) - (b.level_number ?? 99) || (a.order ?? 0) - (b.order ?? 0)
                  );
                  const leveled = sorted.filter((p) => p.level_number);
                  return (
                <div key={track} className="space-y-6">
                  {/* Track header with level path */}
                  <div className="text-center">
                    <h2 className="text-2xl font-display font-bold text-gray-900">
                      {TRACK_LABELS[track]?.icon || '📚'} {TRACK_LABELS[track]?.label || track} Track
                    </h2>
                    {leveled.length > 1 && (
                      <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
                        {leveled.map((p: any, i: number) => (
                          <React.Fragment key={p.id}>
                            {i > 0 && <ArrowRight size={16} className="text-gray-400 flex-shrink-0" />}
                            <span
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white"
                              style={{ backgroundColor: p.color || '#4F46E5' }}
                            >
                              Level {p.level_number} · {p.name}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                    <p className="mt-2 text-sm text-gray-500">
                      Levels are linked — finish one and your child moves up to the next. Start at any level that fits your child.
                    </p>
                  </div>

                  <div className="grid gap-8">
                  {sorted.map((program: any, index: number) => {
                    const scheduleEntries = getProgramScheduleEntries(program);
                    const monthlyPrice = Number(program.monthly_price || program.effective_price || 0);
                    const proratedNow = program.prorated_price_now != null ? Number(program.prorated_price_now) : null;
                    const showProrated =
                      program.payment_type !== 'one_time' &&
                      proratedNow != null &&
                      proratedNow > 0 &&
                      (program.sessions_remaining_this_month ?? 0) > 0 &&
                      proratedNow < monthlyPrice;

                    return (
                    <motion.div
                      key={program.id || index}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="card overflow-hidden"
                    >
                      <div className="grid md:grid-cols-3">
                        <div 
                          className="p-8 text-white flex flex-col justify-center"
                          style={{ backgroundColor: program.color || '#4F46E5' }}
                        >
                          <span className="text-5xl mb-4">{program.color_emoji || '🤖'}</span>
                          {program.level_number && (
                            <span className="inline-block self-start bg-white/25 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide mb-2">
                              Level {program.level_number}
                            </span>
                          )}
                          <h3 className="text-2xl font-bold mb-2">{program.name}</h3>
                          <p className="text-lg opacity-90">Ages {program.min_age}{program.max_age ? `-${program.max_age}` : '+'}</p>
                          {program.next_program_name && (
                            <p className="mt-3 text-sm opacity-90 flex items-center gap-1.5">
                              <TrendingUp size={16} /> Next level: {program.next_program_name}
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-2 p-8">
                          <p className="text-gray-600 mb-6">{program.description}</p>
                          {program.best_for && program.best_for.length > 0 && (
                            <>
                              <h4 className="font-semibold mb-3">Best for:</h4>
                              <ul className="space-y-2 mb-6">
                                {program.best_for.map((item: string, i: number) => (
                                  <li key={i} className="flex items-start">
                                    <CheckCircle size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-600">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                          {program.what_children_experience && program.what_children_experience.length > 0 && (
                            <>
                              <h4 className="font-semibold mb-3">What children experience:</h4>
                              <ul className="space-y-2 mb-6">
                                {program.what_children_experience.map((item: string, i: number) => (
                                  <li key={i} className="flex items-start">
                                    <CheckCircle size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-600">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-3xl font-bold text-primary-600">
                                ${program.effective_price || program.one_time_price || program.monthly_price || settings?.program_price_monthly || '159'}
                                {program.payment_type === 'one_time' ? ' one time' : '/month'}
                              </span>
                              <span className="text-sm text-gray-500">
                                {getProgramScheduleSummary(program)}
                              </span>
                            </div>
                            {showProrated && (
                              <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                                <span className="font-semibold">Join today for ${proratedNow!.toFixed(2)}</span>
                                {' '}— prorated for the {program.sessions_remaining_this_month} session{program.sessions_remaining_this_month === 1 ? '' : 's'} left
                                this month, then ${monthlyPrice.toFixed(2)}/month.
                              </div>
                            )}
                            {program.payment_type !== 'one_time' && program.available_sessions?.length > 0 && (
                              <p className="text-sm text-gray-600">
                                {getProgramScheduleDayTime(program.available_sessions[0])}
                              </p>
                            )}
                            {program.payment_type !== 'one_time' && (
                              <p className="text-xs text-gray-500">
                                Open enrollment — join anytime, even after the program has started.
                              </p>
                            )}
                            {program.payment_type === 'one_time' && (
                              <>
                                <p className="text-sm text-amber-700">
                                  Pay once for this program. No monthly subscription charge.
                                </p>
                                {scheduleEntries.length > 0 && (
                                  <div className="rounded-lg bg-gray-50 p-3 text-sm">
                                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Scheduled Class Dates
                                    </p>
                                    <div className="space-y-2">
                                      {scheduleEntries.map((entry) => (
                                        <div key={entry.key} className="text-gray-700">
                                          <div className="font-medium">{formatScheduleDate(entry.date)}</div>
                                          <div className="text-xs text-gray-500">
                                            {formatScheduleTime(entry.startTime)} - {formatScheduleTime(entry.endTime)}
                                            {entry.location ? ` - ${entry.location}` : ''}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                            <Link to={`/registration?program=${program.id}`} className="btn-primary w-full text-center">
                              Register Now <ArrowRight className="ml-2" size={18} />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                    );
                  })}
                  </div>
                </div>
                  );
                })
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section gradient-bg">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-display font-bold text-white mb-6">
              Ready to Start the Journey?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Enroll your child in our 8-week Saturday program and watch them discover the joy of STEM.
            </p>
            <Link to="/registration" className="btn-secondary text-lg px-8 py-4">
              View Schedule & Register
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;
